import dayjs from 'dayjs'
import { get } from 'lodash/fp'
import PropTypes from 'prop-types'
import { useState } from 'react'

import Table from '~/components/Table'
import { renderStatusPill } from '~/utils/statusPill'

import Dataset from './Dataset.interface'
import DatasetViewerDialog from './DatasetViewerDialog'

const STATUS_PILL_CONFIG = {
  invalid: { type: 'error' },
  ready: { type: 'warning' },
  waiting_for_plate_metadata: { type: 'default', label: 'waiting for plate' },
  in_progress: { type: 'default', label: 'processing' },
  uploaded: { type: 'success' },
  uploading: { type: 'accent' },
  upload_failed: { type: 'error' },
}

interface DatasetTableProps {
  className?: string
  datasets: Dataset[]
  hasMoreData: boolean
  fetchMoreData: () => void
}

const DatasetTable = ({
  className,
  datasets,
  hasMoreData,
  fetchMoreData,
}: DatasetTableProps) => {
  const [selectedDataset, setSelectedDataset] = useState<Dataset | null>(null)
  const [datasetViewerDialogOpen, setDatasetViewerDialogOpen] = useState(false)

  const handleRowClick = row => {
    setSelectedDataset(row)
    setDatasetViewerDialogOpen(true)
  }

  const renderStatus = status => {
    return renderStatusPill(status, STATUS_PILL_CONFIG, {
      small: true,
    })
  }

  const renderBarcode = (row: Dataset) => {
    if (!row.metadata) return null
    if (row.instrumentType === 'cytation') {
      return row.metadata?.plateMetadata?.uuid
    }
    return null
  }

  const renderFilePath = (row: Dataset) => {
    if (!row.metadata) return null
    return row.metadata?.initialDetectedFileMetadata?.filePath
  }

  const tableColumns = [
    {
      name: 'Name',
      width: 200,
      render: get('name'),
      oneLine: true,
      smallText: true,
    },
    {
      name: 'Status',
      width: 150,
      render: (row: Dataset) => renderStatus(get(['status'], row)),
    },
    {
      name: 'Timestamp',
      width: 125,
      render: (row: Dataset) => dayjs(get(['createdAt'], row)).fromNow(),
      smallText: true,
    },
    {
      name: 'Barcode',
      width: 125,
      render: renderBarcode,
      smallText: true,
    },
    {
      name: 'File Path',
      width: 150,
      render: (row: Dataset) => renderFilePath(row),
      smallText: true,
      oneLine: true,
    },
  ]

  return (
    <>
      <Table
        columns={tableColumns}
        data={datasets}
        className={className}
        rowKey='id'
        rowLeftRightPaddingVariant='rowPaddingLow'
        onRowClick={handleRowClick}
        infiniteScrollProps={{
          hasMoreData,
          fetchMoreData,
        }}
      />
      <DatasetViewerDialog
        isOpen={datasetViewerDialogOpen}
        onClose={() => setDatasetViewerDialogOpen(false)}
        datasetId={get('id', selectedDataset)}
      />
    </>
  )
}

DatasetTable.propTypes = {
  className: PropTypes.string,
  datasets: PropTypes.arrayOf(PropTypes.shape({})),
  hasMoreData: PropTypes.bool,
  fetchMoreData: PropTypes.func,
}

export default DatasetTable
